import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import * as CONFIG from "scenes/Settings/scenes/TenantProfileNotificationPreferences/TenantProfileNotificationPreferences.config";

export interface IPreference {
  key: string;
  value: any;
}

interface IDefaultPreference {
  key: string;
  defaultValue: string;
}

/**
 * The two rejection texts, in the order the broker chooses between them
 * (DEV-3325). The second falls back to the first, which is why its keys are
 * paired with the first's rather than read on their own.
 */
export const NOT_ELIGIBLE_TEXTS = [
  {
    nameKey: CONFIG.PREFERENCE_NOT_ELIGIBLE_NAME,
    defaultNameKey: "tenant_profile.notification.not_eligible.default_name.1",
    keys: {
      [ELocale.NL]: [CONFIG.PREFERENCE_NOT_ELIGIBLE_NL],
      [ELocale.FR]: [CONFIG.PREFERENCE_NOT_ELIGIBLE_FR],
      [ELocale.EN]: [CONFIG.PREFERENCE_NOT_ELIGIBLE_EN],
    },
  },
  {
    nameKey: CONFIG.PREFERENCE_NOT_ELIGIBLE_2_NAME,
    defaultNameKey: "tenant_profile.notification.not_eligible.default_name.2",
    keys: {
      [ELocale.NL]: [
        CONFIG.PREFERENCE_NOT_ELIGIBLE_2_NL,
        CONFIG.PREFERENCE_NOT_ELIGIBLE_NL,
      ],
      [ELocale.FR]: [
        CONFIG.PREFERENCE_NOT_ELIGIBLE_2_FR,
        CONFIG.PREFERENCE_NOT_ELIGIBLE_FR,
      ],
      [ELocale.EN]: [
        CONFIG.PREFERENCE_NOT_ELIGIBLE_2_EN,
        CONFIG.PREFERENCE_NOT_ELIGIBLE_EN,
      ],
    },
  },
];

/**
 * The messages for the chosen rejection text, falling through that text's
 * keys in order.
 *
 * Kept apart from the modal so the fallback can be tested without rendering
 * anything: it is the part an office notices, since it decides whether a
 * rejection written before this release still goes out unchanged.
 */
export const getNotEligibleLocaleMessages = (
  getPreferenceValue: (key: string) => string | undefined,
  textIndex: number,
) => {
  const text = NOT_ELIGIBLE_TEXTS[textIndex] || NOT_ELIGIBLE_TEXTS[0];
  const firstFilled = (keys: string[]) =>
    keys.map(key => getPreferenceValue(key)).find(Boolean);

  return {
    [ELocale.NL]: firstFilled(text.keys[ELocale.NL]),
    [ELocale.FR]: firstFilled(text.keys[ELocale.FR]),
    [ELocale.EN]: firstFilled(text.keys[ELocale.EN]),
  };
};

export const preferences: IDefaultPreference[] = [
  {
    key: CONFIG.PREFERENCE_NOT_ELIGIBLE_NL,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.not_eligible.placeholder",
      undefined,
      undefined,
      ELocale.NL,
    ),
  },
  {
    key: CONFIG.PREFERENCE_NOT_ELIGIBLE_FR,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.not_eligible.placeholder",
      undefined,
      undefined,
      ELocale.FR,
    ),
  },
  {
    key: CONFIG.PREFERENCE_NOT_ELIGIBLE_EN,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.not_eligible.placeholder",
      undefined,
      undefined,
      ELocale.EN,
    ),
  },

  // The second rejection text and the two names, with no default of their
  // own: an empty value is what lets the second text fall back to the first,
  // and an empty name is what shows the generic second-text label (DEV-3325).
  { key: CONFIG.PREFERENCE_NOT_ELIGIBLE_2_NL, defaultValue: "" },
  { key: CONFIG.PREFERENCE_NOT_ELIGIBLE_2_FR, defaultValue: "" },
  { key: CONFIG.PREFERENCE_NOT_ELIGIBLE_2_EN, defaultValue: "" },
  { key: CONFIG.PREFERENCE_NOT_ELIGIBLE_NAME, defaultValue: "" },
  { key: CONFIG.PREFERENCE_NOT_ELIGIBLE_2_NAME, defaultValue: "" },

  {
    key: CONFIG.PREFERENCE_VISITOR_NL,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.visitor.placeholder",
      undefined,
      undefined,
      ELocale.NL,
    ),
  },
  {
    key: CONFIG.PREFERENCE_VISITOR_FR,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.visitor.placeholder",
      undefined,
      undefined,
      ELocale.FR,
    ),
  },
  {
    key: CONFIG.PREFERENCE_VISITOR_EN,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.visitor.placeholder",
      undefined,
      undefined,
      ELocale.EN,
    ),
  },

  {
    key: CONFIG.PREFERENCE_PLAN_FOR_VISIT_NL,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.plan_for_visit.placeholder",
      undefined,
      undefined,
      ELocale.NL,
    ),
  },
  {
    key: CONFIG.PREFERENCE_PLAN_FOR_VISIT_FR,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.plan_for_visit.placeholder",
      undefined,
      undefined,
      ELocale.FR,
    ),
  },
  {
    key: CONFIG.PREFERENCE_PLAN_FOR_VISIT_EN,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.plan_for_visit.placeholder",
      undefined,
      undefined,
      ELocale.EN,
    ),
  },

  {
    key: CONFIG.PREFERENCE_WAITING_LIST_NL,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.waiting_list.placeholder",
      undefined,
      undefined,
      ELocale.NL,
    ),
  },
  {
    key: CONFIG.PREFERENCE_WAITING_LIST_FR,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.waiting_list.placeholder",
      undefined,
      undefined,
      ELocale.FR,
    ),
  },
  {
    key: CONFIG.PREFERENCE_WAITING_LIST_EN,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.waiting_list.placeholder",
      undefined,
      undefined,
      ELocale.EN,
    ),
  },

  {
    key: CONFIG.PREFERENCE_CANDIDATE_TENANT_NL,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.candidate_tenant.placeholder",
      undefined,
      undefined,
      ELocale.NL,
    ),
  },
  {
    key: CONFIG.PREFERENCE_CANDIDATE_TENANT_FR,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.candidate_tenant.placeholder",
      undefined,
      undefined,
      ELocale.FR,
    ),
  },
  {
    key: CONFIG.PREFERENCE_CANDIDATE_TENANT_EN,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.candidate_tenant.placeholder",
      undefined,
      undefined,
      ELocale.EN,
    ),
  },

  {
    key: CONFIG.PREFERENCE_INVITED_NL,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.invited.placeholder",
      undefined,
      undefined,
      ELocale.NL,
    ),
  },
  {
    key: CONFIG.PREFERENCE_INVITED_FR,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.invited.placeholder",
      undefined,
      undefined,
      ELocale.FR,
    ),
  },
  {
    key: CONFIG.PREFERENCE_INVITED_EN,
    defaultValue: getLocalizedText(
      "tenant_profile.notification.invited.placeholder",
      undefined,
      undefined,
      ELocale.EN,
    ),
  },
];
